import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  CalendarDays,
  HelpCircle,
  BadgeDollarSign,
  CalendarClock,
  Banknote,
  HandCoins,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Numeral from "../../../components/numeral/Numeral";
import { HttpRequest } from "../../../services/httpRequest";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { CreditUserContext } from "../../../context/CreditUser/CreditUserContext";
import { returnToSignin } from "../../../utils/returnToSign";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { useCredit } from "../../../context/creditContext";
import { IndexedDBService } from "../../../services/indexedDbService";
import {
  determinaCreditoAMostrar,
  formatFecha,
  loadCredits,
} from "../credits/utils";
import { SkeletonPayloginPage } from "./components/skeleton_paylogin_page";
import { ErrorAlert } from "../../../components/alerts/errorAlert";
import { checkCreditPayable, getPaymentAvailability } from "./helpers/payment-gate";
import { useMobileDetection } from "../../../hooks/use-mobile-detection";
import { ChargesTooltip } from "./components/ChargesTooltip";
import { Primary } from "../../../components/buttons/Primary";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import {
  resolverDeudaHoy,
  resolverNovacionYAbonos,
} from "../../../utils/deuda.utils";
import { calcularFechas } from "../../../components/credits/calculoFechas";

const indexedDbService = new IndexedDBService();

export function NewPayLoginPage(props) {
  const { updateDeudaHoyBackend, updateCreditUser } =
    useContext(CreditUserContext);
  const { disclosures, creditInfo, due } = useCredit();
  let navigate = useNavigate();
  const [basicInfo, setBasicInfo] = useState({});
  const [creditsInfo, setCreditsInfo] = useState({});
  const [, setUserInfoAuth] = useState({});
  const [coststotaltes, setCoststotaltes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deudaAFecha, setDeudaAFecha] = useState(0);
  const [, setInfoCupo] = useState({});
  const [hasSaldoCapital, setHasSaldoCapital] = useState(true);
  const [hasCreditVigent, setHasCreditVigent] = useState(false);
  const [dateNew, setDateNew] = useState("");
  const [totalNovacion, setTotalNovacion] = useState(null);
  const [costsNovacion, setCostsNovacion] = useState([]);
  const [totalAbonos, setTotalAbonos] = useState(null);
  const [abonos, setAbonos] = useState(null);
  const [, setIsMobile] = useState(false);
  const [approvedQuota, setApprovedQuota] = useState(null);
  // Lo que el backend permite mostrar (ver getPaymentAvailability): `paymentBlockedMessage` no nulo = el
  // crédito aún no puede pagar y no se muestra ninguna opción; `novationAvailable` false = fuera de la
  // ventana de novación y se oculta solo "Pago mínimo". Arrancan permisivos: si la consulta falla se
  // muestra todo y cada clic vuelve a validar.
  const [paymentBlockedMessage, setPaymentBlockedMessage] = useState(null);
  const [novationAvailable, setNovationAvailable] = useState(true);
  const [saldoCapital, setSaldoCapital] = useState(null);
  // { message, key } — la key fuerza a ErrorAlert a montarse de nuevo si el cliente vuelve a intentarlo.
  const [paymentAlert, setPaymentAlert] = useState(null);
  const { isDesktop } = useMobileDetection();

  // Antes de dejar al cliente elegir método de pago, el backend confirma que el crédito puede recibir
  // este pago (crédito nuevo sin desembolso Exitoso no puede; la novación además exige estar entre 8 días
  // antes y 30 días después de la fecha límite). `hasNovation` = true solo para "Pago mínimo (Novación)"; el pago total y
  // el abono van con false. Solo bloquea con un rechazo de negocio; ante un fallo técnico deja seguir,
  // porque el paso final (/medios-de-pago) vuelve a validar en el backend.
  const runIfCreditPayable = async (action, hasNovation = false) => {
    setPaymentAlert(null);
    const payable = await checkCreditPayable(undefined, hasNovation);
    if (!payable.ok && payable.blocked) {
      setPaymentAlert({ message: payable.message, key: Date.now() });
      return;
    }
    action();
  };

  const loadCreditById = useCallback(async () => {
    try {
      const data = await HttpRequest.getCreditById(
        localStorage.getItem("credit_id"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUserAuth = useCallback(async () => {
    try {
      const data = await HttpRequest.getUserAuth(localStorage.getItem("doc"));
      return data.message.user;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUser = useCallback(async () => {
    try {
      const data = await HttpRequest.getInfoUser(localStorage.getItem("doc"));
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoQuota = useCallback(async () => {
    try {
      const data = await HttpRequest.getQuotaStatus(
        localStorage.getItem("doc"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const configData = useCallback(async (credit, infoCupo) => {
    try {
      const result = await resolverDeudaHoy(credit, infoCupo); // ← await + destructuring correcto

      if (result) {
        setCoststotaltes(result.costsFeature);
        setDeudaAFecha(result.deudaHoy);

        setApprovedQuota(parseInt(credit.approvedQuota));
        const saldoCapital = result.costsFeature.find(
          (cost) => cost.item === "Saldo a capital",
        );
        setSaldoCapital(saldoCapital.value);
        updateDeudaHoyBackend(result.infoDeudaBackend);
      } else {
        setHasSaldoCapital(false);
      }
    } catch (error) {
      console.log(error);
    }
  }, [updateDeudaHoyBackend]);
  const handleUpdateDataIndexDb = useCallback(async (data) => {
    try {
      let item = await indexedDbService.findItem(
        "id",
        localStorage.getItem("credit_id"),
      );
      if (item) {
        indexedDbService.updateItem(item.id, data);
      } else {
        await indexedDbService.addItem({
          ...data,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const determineIfCreditIsPendingToSign = useCallback(async () => {
    try {
      let credits = await loadCredits();
      let currentCredit = determinaCreditoAMostrar(credits);
      if (currentCredit[0]?.status === "Pendiente de firmar") {
        return navigate("/request-credit?renovar=true");
      }
    } catch (error) {
      console.log(error);
      return;
    }
  }, [navigate]);

  const HandleSetCostsFeaturesAndTotalPay = useCallback(async (credit) => {
    try {
      let infoCupo = await loadInfoQuota();

      const { costsNovacion, totalNovacion, conceptosAbonos, totalAbonos } =
        await resolverNovacionYAbonos(credit, infoCupo); // ← await + destructuring correcto

      setCostsNovacion(costsNovacion);
      setTotalNovacion(totalNovacion);
      setAbonos(conceptosAbonos); // ← faltaba
      setTotalAbonos(totalAbonos); // ← faltaba
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [loadInfoQuota]);
  useEffect(() => {
    const loadQuotaStatus = async () => {
      let quotaStatus = await loadInfoQuota();
      return quotaStatus;
    };

    const validSessionAndTokenJwt = async () => {
      try {
        let isValidToken = await validateSessionAndJwtToken();
        return isValidToken;
      } catch (error) {
        return false;
      }
    };

    const _loadCreditById = async () => {
      let credit = await loadCreditById();
      return credit;
    };

    const loadUserAuth = async () => {
      let userAuth = await loadInfoUserAuth();
      return userAuth;
    };

    const loadInfo = async () => {
      let info = await loadInfoUser();
      return info;
    };

    const tieneCreditoVigente = (creditos = []) => {
      let creditosVigentes = creditos.filter(
        (credito) => credito?.status === "Vigente",
      );
      if (creditosVigentes.length === 0) {
        return false;
      } else {
        return true;
      }
    };

    validSessionAndTokenJwt()
      .then(async (isValidToken) => {
        let info;
        let userAuth;
        let creditData;
        if (isValidToken) {
          try {
            let fechas = calcularFechas();
            let dateNew = fechas.fecha2.format("DD/MM/YYYY");
            setDateNew(dateNew);

            determineIfCreditIsPendingToSign();
            userAuth = await loadUserAuth();
            info = await loadInfo();
            setBasicInfo(info);
            setUserInfoAuth(userAuth);
            let cupo = await loadQuotaStatus();
            setInfoCupo(cupo);
            creditData = await _loadCreditById();
            let existeCreditoVigente = tieneCreditoVigente([creditData]);
            setHasCreditVigent(existeCreditoVigente);
            if (!creditData) {
              alert("No tienes creditos activos.");
            } else {
              setCreditsInfo(creditData);
              updateCreditUser(creditData);
              configData(creditData, cupo);
            }

            let creditUser = await loadCreditById(
              localStorage.getItem("credit_id"),
            );
            await HandleSetCostsFeaturesAndTotalPay(creditUser);
            const availability = await getPaymentAvailability();
            setPaymentBlockedMessage(availability.canPay ? null : availability.message);
            setNovationAvailable(availability.canNovate);
            setIsLoading(false);
          } catch (error) {
            console.log(error);
            return;
          }
        } else {
          returnToSignin(navigate);
        }
      })
      .catch((e) => {
        returnToSignin(navigate);
      });
  }, [
    navigate,
    updateCreditUser,
    loadInfoQuota,
    loadCreditById,
    loadInfoUserAuth,
    loadInfoUser,
    determineIfCreditIsPendingToSign,
    configData,
    HandleSetCostsFeaturesAndTotalPay,
  ]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  if (isLoading) {
    return <SkeletonPayloginPage title={"¿Cuánto quieres pagar?"} />;
  }

  return (
    <>
      {paymentAlert && (
        <ErrorAlert
          key={paymentAlert.key}
          isOpen={true}
          message={paymentAlert.message}
          isDesktop={isDesktop}
        />
      )}
      <DashboardMyCredit
        name={basicInfo?.firstname ?? ""}
        balance={creditsInfo?.status === "Cancelado" ? 0 : deudaAFecha}
        current={"2"}
        disclosures={disclosures}
        title={"¿Cuánto quieres pagar?"}
        creditInfo={creditInfo}
        due={due}
        justify={"center"}
        flexWrap={"wrap"}
        display={"flex"}
        height={"97%"}
        main={
          <>
            <div
              className={`flex flex-col justify-center flex-1 2xl:flex-none 2xl:h-auto w-auto bg-white px-8 py-5 rounded-3xl shadow-lg mr-2 ml-2 md:mr-0 md:ml-0 mt-8 mb-40 md:mb-0 md:w-125`}
            >
              {/* Header */}

              {creditsInfo?.status === "Cancelado" ||
                creditsInfo?.status === "Solicitud rechazada" ? (
                <div
                  className="text-center flex flex-col"
                  style={{ gap: "25px" }}
                >
                  <p style={{ fontSize: "25px" }}>
                    No tienes créditos activos en este momento
                  </p>
                  <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8">
                    <a href="/renovar">
                      <Primary
                        text={"Consultar cupo"}
                        icon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                          </svg>
                        }
                      />
                    </a>
                  </div>
                </div>
              ) : hasSaldoCapital === false ? (
                <div
                  className="text-center flex flex-col"
                  style={{ gap: "25px" }}
                >
                  <p style={{ fontSize: "25px" }}>
                    Lo sentimos. ha ocurrido un error al consultar tu deuda a hoy,
                    por favor comunicate con el área de soporte para revisar a
                    fondo, gracias por tu comprensión.
                  </p>
                  <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8"></div>
                </div>
              ) : !hasCreditVigent ? (
                <div
                  className="text-center flex flex-col"
                  style={{ gap: "25px" }}
                >
                  <p style={{ fontSize: "25px" }}>
                    No tienes un crédito vigente en este momento
                  </p>
                  <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8">
                    <a href="/renovar">
                      <Primary
                        text={"Consultar cupo"}
                        icon={<ArrowLongRightIcon className="w-6 h-auto" />}
                      />
                    </a>
                  </div>
                </div>
              ) : paymentBlockedMessage ? (
                <div className="flex flex-col items-center justify-center text-center w-full px-6 py-4 max-w-md mx-auto">
                  {/* Ilustración */}
                  <img
                    src="/png/blocked_payment.jpg"
                    alt="Pago bloqueado"
                    className="w-full max-w-[480px] h-auto object-contain"
                  />

                  {/* Contenedor del texto con margen negativo para eliminar el espacio vacío de la imagen */}
                  <div className="w-full -mt-8 sm:-mt-12 position-relative z-10">
                    <p className="text-lg sm:text-[24px] font-semibold text-[#1A1A1A] leading-snug max-w-[90%] mx-auto">
                      {paymentBlockedMessage}
                    </p>
                  </div>
                </div>

              ) : (
                <div className="space-y-6">
                  {/* <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      ¿Cuánto quieres pagar?
                    </h2>
                    <p className="text-sm text-gray-500 mt-2">
                      Crédito{" "}
                      <span className="font-semibold">
                        #{creditsInfo?.credit_id}
                      </span>
                    </p>
                  </div> */}

                  {/* Card Deuda actual */}
                  {/* {!isMobile && (
                    <div className="rounded-2xl bg-[#761feb] from-purple-600 to-violet-500 p-6 shadow-lg">
                      <p className="text-sm text-white/80">Deuda actual</p>
                      <div className="mt-2 text-3xl font-bold text-white">
                        <Numeral format={"$0,0"} value={deudaAFecha} />
                      </div>
                    </div>
                  )} */}

                  {/* Opciones de pago */}
                  <div className="flex-1 2xl:flex-none  overflow-y-auto space-y-4 pt-4">
                    {/* Fecha límite */}
                    <div className="flex flex-row items-center gap-3 rounded-xl bg-[#f4f4f9] px-4 py-3 text-sm text-gray-700">
                      <div>
                        <CalendarDays className="size-5" strokeWidth={2} />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-gray-500">
                          Fecha límite de pago{" "}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatFecha(creditsInfo?.paydayLimit)}
                        </span>
                      </div>
                    </div>
                    {totalAbonos > 0 ? (
                      <div className="flex flex-row items-center justify-between rounded-xl bg-[#ecfdf5] pl-4 pr-[1.3rem] py-3 text-sm text-gray-700">
                        <div className="flex flex-row gap-5">
                          <BadgeDollarSign
                            className="size-5"
                            style={{ color: "#007a55" }}
                            strokeWidth={2}
                          />
                          <span
                            className="text-gray-900"
                            style={{ color: "#007a55" }}
                          >
                            {`Tienes`}{" "}
                            <Numeral
                              className="font-semibold text-sm"
                              format={"$0,0"}
                              value={totalAbonos}
                            />{" "}
                            {`en abonos previos`}
                          </span>
                        </div>

                        <ChargesTooltip
                          icon={
                            <HelpCircle
                              className="size-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                              strokeWidth={2}
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          title={"¿En que se ha utilizado mis abonos?"}
                          items={abonos.map((c) => ({
                            label: c.concept,
                            value: c.total,
                          }))}
                        />
                      </div>
                    ) : (
                      <></>
                    )}

                    {/* Pago mínimo (Novación) */}
                    {totalNovacion > 0 && novationAvailable ? (
                      <>
                        {saldoCapital === approvedQuota && (
                          <div
                            style={{
                              border: "1px solid #f1f0f0",
                              padding: "5px 10px",
                              gap: "5px",
                            }}
                            className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-xs hover:shadow-md transition"
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red text-white">
                                <CalendarClock
                                  className="size-5"
                                  strokeWidth={2}
                                />
                              </div>

                              <div>
                                <p className="text-gray-900 flex flex-row items-center gap-1">
                                  Pago mínimo (Novación)
                                </p>

                                <Numeral
                                  className="font-semibold text-xl"
                                  format={"$0,0"}
                                  value={totalNovacion}
                                />

                                <p className="text-sm text-[#99a1af]">
                                  Tu próxima fecha de pago
                                </p>

                                <p className="text-sm font-bold text-[#99a1af]">
                                  {formatFecha(dateNew)}
                                </p>
                              </div>
                            </div>

                            <div
                              className="flex flex-col items-center gap-4 py-5"
                              style={{
                                marginRight: totalNovacion === 0 ? "12px" : 0,
                              }}
                            >
                              <ChargesTooltip
                                icon={
                                  <HelpCircle
                                    className="size-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                                    strokeWidth={2}
                                  />
                                }
                                textInfo={
                                  "La definición de novar crédito por el código civil consiste en sustituir un crédito por uno nuevo. Para que esto se pueda hacer efectivo en Galilea, debes pagar los cargos asociados a tu crédito a excepción del capital"
                                }
                                items={costsNovacion.map((c) => ({
                                  label: c.item,
                                  value: c.value,
                                }))}
                              />
                              <div className="grid justify-items-center lg:pt-4">
                                <button
                                  type="button"
                                  className={`flex justify-center p-2 rounded-full shadow-xs text-md text-white font-medium bg-purple hover:bg-purpleDark cursor-pointer`}
                                  onClick={() =>
                                    runIfCreditPayable(() => {
                                      handleUpdateDataIndexDb({
                                        amount: String(totalNovacion),
                                        hasExtend: true,
                                        dateExtend: dateNew,
                                        id: localStorage.getItem("credit_id"),
                                        name: basicInfo?.firstname ?? "",
                                        lastname: basicInfo.firstlastname,
                                      });

                                      navigate("/medios-de-pago");
                                    }, true)
                                  }
                                >
                                  <ChevronRightIcon className="w-6 h-auto" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <></>
                    )}

                    {/* Pago total */}
                    <div
                      style={{
                        border: "1px solid #f1f0f0",
                        padding: "5px 10px",
                        gap: "5px",
                      }}
                      className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-xs hover:shadow-md transition"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1e1b4b] text-white">
                          <Banknote className="size-5" strokeWidth={2} />
                        </div>

                        <div>
                          <p className="text-gray-900 flex flex-row items-center gap-1">
                            Pago total
                          </p>

                          <Numeral
                            className="font-semibold text-xl"
                            format={"$0,0"}
                            value={deudaAFecha}
                          />

                          <p className="text-sm text-[#99a1af]">
                            Total hasta hoy, quedas a paz y salvo y acumulas puntos
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center gap-4 py-5">
                        <ChargesTooltip
                          icon={
                            <HelpCircle
                              className="size-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                              strokeWidth={2}
                            />
                          }
                          items={coststotaltes.map((c) => ({
                            label: c.item,
                            value: c.value,
                          }))}
                          isInfo={false}
                        />
                        <div className="grid justify-items-center lg:pt-4">
                          <button
                            type="button"
                            className={`flex justify-center p-2 rounded-full shadow-xs text-md text-white font-medium bg-purple hover:bg-purpleDark cursor-pointer`}
                            onClick={() =>
                              runIfCreditPayable(() => {
                                handleUpdateDataIndexDb({
                                  amount: String(deudaAFecha),
                                  hasExtend: false,
                                  dateExtend: null,
                                  id: localStorage.getItem("credit_id"),
                                  name: basicInfo?.firstname ?? "",
                                  lastname: basicInfo.firstlastname,
                                });

                                navigate("/medios-de-pago");
                              }, false)
                            }
                          >
                            <ChevronRightIcon className="w-6 h-auto" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Abonar a deuda */}
                    {deudaAFecha >= 3000 && (
                      <div
                        style={{
                          border: "1px solid #f1f0f0",
                          padding: "15px 10px",
                        }}
                        className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-xs hover:shadow-md transition"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDEDF5] text-gray-600">
                            <HandCoins className="size-5" strokeWidth={2} />
                          </div>

                          <div>
                            <p className="text-gray-900">
                              Abona a tu deuda total
                            </p>
                            <p className="text-sm text-[#99a1af]">
                              Esto no cambia la fecha límite ni te da más tiempo para pagar
                            </p>
                          </div>
                        </div>
                        <div className="grid justify-items-center">
                          <button
                            type="button"
                            className={`flex justify-center p-2 rounded-full shadow-xs text-md text-white font-medium bg-purple hover:bg-purpleDark cursor-pointer`}
                            onClick={() =>
                              runIfCreditPayable(() => navigate("/abonar"))
                            }
                          >
                            <ChevronRightIcon className="w-6 h-auto" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        }
      />
    </>
  );
}
